// Domain activation feedback V1; 2026-10-31 23:45:42 CST; parent a84ae1fee6f6d8b4bbb6b307f09cfaa487b85c4e.
// Domain publication compatibility V1; 2026-10-31 23:12:19 CST; parent 81bdb256d5b7a94bb5a1265d806ce53aef0e0494.
// Console 0.1.150; 2026-10-22 14:52 CST; parent ee28d82c0f6d228650bdffd99d530f5227d8e70c. A bounded ACK wait ending pending is not a failed release.
import { consoleApi } from '../api/client';

/*
 * Auto-publish helper:
 * This deliberately publishes one domain at a time after a page saves that domain's config.
 * Keep this narrow. Do not convert it into a delayed global batch publish, because operators expect "change where, publish where".
 */
export interface AutoPublishResult {
  ok: boolean;
  pending?: boolean;
  packageId?: number;
  message: string;
}

export async function publishCurrentDomainConfig(
  domainId: number | undefined,
  reason: string,
  verifyNodeRelease = false
): Promise<AutoPublishResult | null> {
  if (!domainId) {
    return null;
  }
  try {
    // 当前域名单域发布确认，更新时间：2026-08-30 22:56 CST。
    // 用户点击“确认提交”已明确同意保存当前域名配置，这里只确认并生成该域名的配置包，不扩散到其他域名。
    const pkg = await consoleApi.publish.generatePackage(domainId, {
      acknowledged: true,
      actionLabel: '生成当前域名配置包',
      reason,
      impactPreview: [`仅生成域名 ID ${domainId} 的配置包`, '不会修改或发布其他域名配置']
    });
    await consoleApi.publish.createDraft(domainId, reason);
    // 兼容边界：旧页面继续维持原有“生成包 + 草稿”流程；只有新策略工作区
    // 显式要求节点验收时，才创建正式 Release 并等待 ACK。
    if (!verifyNodeRelease) {
      return {
        ok: true,
        pending: true,
        packageId: typeof pkg.id === 'number' ? pkg.id : undefined,
        message: `配置包与发布草稿已保存，等待节点发布确认${pkg.id ? `（配置包 ${pkg.id}）` : ''}`
      };
    }
    if (typeof pkg.id !== 'number') {
      throw new Error('配置包生成成功但未返回编号');
    }
    const release = await consoleApi.security.publishStrategyDomain(domainId, {
      packageId: pkg.id,
      reason
    });
    const completed = await waitForRelease(release.id);
    if (!['SUCCEEDED', 'FAILED', 'PARTIAL', 'PARTIAL_FAILED', 'ROLLED_BACK'].includes(completed.status || '')) {
      return { ok: false, pending: true, packageId: pkg.id, message: '配置已提交，等待节点确认' };
    }
    if (completed.status !== 'SUCCEEDED') {
      throw new Error(completed.lastError || `节点发布状态：${completed.status || 'UNKNOWN'}`);
    }
    return {
      ok: true,
      packageId: pkg.id,
      message: `已保存并发布当前域名配置（配置包 ${pkg.id}）`
    };
  } catch (e) {
    return {
      ok: false,
      message: `已保存，但自动发布当前域名配置失败：${e instanceof Error ? e.message : '请稍后重试'}`
    };
  }
}

/** 最多等待 90 秒，覆盖节点约 60 秒的轮询周期，并以 ACK 作为真正成功依据。 */
async function waitForRelease(releaseId: number) {
  let latest;
  for (let attempt = 0; attempt < 90; attempt += 1) {
    const release = await consoleApi.publish.release(releaseId);
    latest = release;
    if (['SUCCEEDED', 'FAILED', 'PARTIAL', 'PARTIAL_FAILED', 'ROLLED_BACK'].includes(release.status || '')) {
      return release;
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
  return latest!;
}
